/**
 * The parts of a protocol or cipher suite, each with its own quantum view.
 *
 * A protocol endpoint or cipher suite is rated by its weakest part: one whose key exchange a quantum computer breaks
 * (ECDHE, X25519, RSA) is quantum-vulnerable, though the encryption (AES-256-GCM) and hash (SHA-384) inside it stay
 * safe. Showing the parts beside the rating says why the row reads as it does.
 */
export type PartTone = 'safe' | 'acceptable' | 'deprecated' | 'vulnerable' | 'insecure' | 'unknown';
export interface CryptoPart { role: 'Key exchange' | 'Authentication' | 'Encryption' | 'Hash'; name: string; tone: PartTone }

export const PART_LABEL: Record<PartTone, string> = {
  safe: 'quantum-safe', acceptable: 'acceptable; prefer 256-bit', deprecated: 'deprecated; acceptable as HMAC', vulnerable: 'quantum-vulnerable', insecure: 'insecure today', unknown: 'not rated',
};

/** The quantum view of one algorithm name, by what it is. */
export function partTone(role: CryptoPart['role'], name: string): PartTone {
  const n = name.toLowerCase();
  if (role === 'Key exchange' || role === 'Authentication') {
    if (/ml-?kem|kyber|ml-?dsa|slh-?dsa|falcon|sphincs/.test(n)) return 'safe';
    if (/x25519|x448|ecdh|\bdhe?\b|ffdhe|\bdh\b|rsa|ecdsa|\bdsa\b|\bdss\b|ed25519|ed448|secp|p-?256|p-?384|p-?521|curve|ec\b|psk/.test(n)) return /psk/.test(n) && !/dhe|ecdhe|rsa/.test(n) ? 'unknown' : 'vulnerable';
    return 'unknown';
  }
  if (role === 'Encryption') {
    if (/3des|des-?ede|\bdes\b|des-cbc|rc4|rc2|arcfour|null|export/.test(n)) return 'insecure';
    if (/aes[-_ ]?256|chacha20/.test(n)) return 'safe';
    if (/aes|camellia|aria/.test(n)) return 'acceptable';
    return 'unknown';
  }
  // In TLS and SSH the hash is an HMAC: SHA-1 there is deprecated, not broken (as the reports say); MD5 is insecure.
  if (/md5/.test(n)) return 'insecure';
  if (/sha-?1(?!\d)|^sha$|\bsha\b/.test(n)) return 'deprecated';
  if (/sha-?(224|256|384|512)|sha2|sha3|poly1305|aead|gcm|ccm/.test(n)) return 'safe';
  return 'unknown';
}

const split = (v: unknown) => (typeof v === 'string' ? v.split(/\s*[,;|]\s*/).map((x) => x.trim()).filter(Boolean) : []);

/** The parts a protocol or cipher-suite row records, rated; empty when it records none (often TLS 1.3 endpoints). */
export function cryptoParts(row: Record<string, unknown>): CryptoPart[] {
  const cols: Array<[CryptoPart['role'], string[]]> = [
    ['Key exchange', ['kex_algorithms', 'kex_algorithm']],
    ['Authentication', ['authentication_algorithms', 'authentication_algorithm']],
    ['Encryption', ['encryption_algorithms', 'bulk_encryption_algorithm']],
    ['Hash', ['mac_algorithms', 'mac_or_aead']],
  ];
  const out: CryptoPart[] = [];
  for (const [role, keys] of cols) {
    const names = [...new Set(keys.flatMap((k) => split(row[k])))];
    for (const name of names) out.push({ role, name, tone: partTone(role, name) });
  }
  return out;
}
